import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiCalendar, FiPrinter } from 'react-icons/fi';
import { HiDocumentReport } from 'react-icons/hi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';

// Componente de Tarjeta de Resumen
function TarjetaMetrica({ titulo, valor, colorBorde, colorTexto, icono: Icono }) {
  return (
    <div className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-600 font-medium mb-1">{titulo}</p>
      <p className={`text-xl font-bold ${colorTexto}`}>{valor}</p>
    </div>
  );
}

export default function MovimientosDia() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(() => {
    // Fecha actual LOCAL en formato YYYY-MM-DD (sin conversión a UTC)
    const hoy = new Date();
    const yyyy = hoy.getFullYear();
    const mm = String(hoy.getMonth() + 1).padStart(2, '0');
    const dd = String(hoy.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  useEffect(() => {
    cargarDatos();
  }, [fechaSeleccionada]);

  const cargarDatos = async () => {
    try {
      setCargando(true);
      const response = await instanciaAxios.get(`/reporte/movimientos-dia?fecha=${fechaSeleccionada}`);
      setDatos(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar movimientos del día:', error);
      toast.error('Error al cargar el reporte');
    } finally {
      setCargando(false);
    }
  };

  const formatMonto = (monto) => {
    return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    // Parsear YYYY-MM-DD como fecha LOCAL (no UTC) para evitar mostrar el día anterior
    const [y, m, d] = fecha.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleImprimir = () => {
    window.print();
  };

  if (cargando && !datos) {
    return <SpinnerCargando />;
  }

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-8">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-8 w-full max-w-7xl mx-6">
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <HiDocumentReport className="text-color-primario text-3xl" />
                  <h1 className="font-cascadia font-bold text-2xl">Movimientos del Día</h1>
                </div>
                <p className="text-gray-600 text-base">
                  Resumen consolidado de todas las cajas - {formatFecha(fechaSeleccionada)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleImprimir}
                  className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition-colors print:hidden"
                  title="Imprimir reporte"
                >
                  <FiPrinter size={18} />
                  Imprimir
                </button>
                <button
                  onClick={cargarDatos}
                  disabled={cargando}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors print:hidden"
                  title="Actualizar"
                >
                  <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {/* Selector de Fecha */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md p-4 print:hidden">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <FiCalendar className="text-lg" />
                  <label className="font-medium text-base">Fecha:</label>
                </div>
                <input
                  type="date"
                  value={fechaSeleccionada}
                  onChange={(e) => setFechaSeleccionada(e.target.value)}
                  className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                />
              </div>
            </div>

            {datos && (
              <>
                {/* RESUMEN EJECUTIVO */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Resumen Ejecutivo</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <TarjetaMetrica
                      titulo="Total de Ventas"
                      valor={`RD$ ${formatMonto(datos.metricas_generales.total_ventas)}`}
                      colorBorde="border-green-500"
                      colorTexto="text-green-700"
                    />
                    <TarjetaMetrica
                      titulo="Total de Ingresos"
                      valor={`RD$ ${formatMonto(datos.metricas_generales.total_ingresos)}`}
                      colorBorde="border-blue-500"
                      colorTexto="text-blue-700"
                    />
                    <TarjetaMetrica
                      titulo="Total de Egresos"
                      valor={`RD$ ${formatMonto(datos.metricas_generales.total_egresos)}`}
                      colorBorde="border-red-500"
                      colorTexto="text-red-700"
                    />
                    <TarjetaMetrica
                      titulo="Ventas a Crédito"
                      valor={`RD$ ${formatMonto(datos.metricas_generales.ventas_credito)}`}
                      colorBorde="border-orange-500"
                      colorTexto="text-orange-700"
                    />
                  </div>
                </div>

                {/* ANÁLISIS DE VENTAS */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Análisis de Ventas</h2>
                  
                  {/* Ventas por Método de Pago */}
                  {datos.metodos_pago && datos.metodos_pago.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                      <h3 className="font-cascadia text-base font-semibold text-gray-700 mb-4">
                        Por Método de Pago
                      </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                              Método
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                              Monto Total
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                              Transacciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {datos.metodos_pago.map((metodo, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {metodo.metodo_pago}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                                RD$ {formatMonto(metodo.monto_total)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                <span className="px-2 py-1 bg-gray-100 rounded-lg">
                                  {metodo.cantidad_transacciones}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                  )}

                  {/* Ventas por Categoría */}
                  {datos.categorias && datos.categorias.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                      <h3 className="font-cascadia text-base font-semibold text-gray-700 mb-4">
                        Por Categoría de Producto
                      </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                              Categoría
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                              Unidades Vendidas
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                              Monto Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {datos.categorias.map((cat, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {cat.categoria}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                {parseFloat(cat.unidades_vendidas).toLocaleString('es-DO')}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                                RD$ {formatMonto(cat.monto_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                  )}
                </div>

                {/* CONTROL DE CAJA */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Control de Caja</h2>
                  
                  <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                    <h3 className="font-cascadia text-base font-semibold text-gray-700 mb-4">
                      Diferencias en Cierres del Día
                    </h3>
                  {datos.diferencias_caja.cajas.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                Caja
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                                Usuario
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                                Esperado
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                                Real
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                                Diferencia
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {datos.diferencias_caja.cajas.map((caja, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {caja.nombre_caja}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {caja.usuario}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                  RD$ {formatMonto(caja.monto_esperado)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                  RD$ {formatMonto(caja.monto_real)}
                                </td>
                                <td className={`px-4 py-3 text-sm text-right font-semibold ${
                                  parseFloat(caja.diferencia) === 0 ? 'text-green-600' :
                                  parseFloat(caja.diferencia) > 0 ? 'text-blue-600' : 'text-red-600'
                                }`}>
                                  RD$ {formatMonto(caja.diferencia)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100">
                            <tr>
                              <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-900">
                                TOTALES
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                RD$ {formatMonto(datos.diferencias_caja.totales.esperado)}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                RD$ {formatMonto(datos.diferencias_caja.totales.real)}
                              </td>
                              <td className={`px-4 py-3 text-sm font-bold text-right ${
                                parseFloat(datos.diferencias_caja.totales.diferencia) === 0 ? 'text-green-600' :
                                parseFloat(datos.diferencias_caja.totales.diferencia) > 0 ? 'text-blue-600' : 'text-red-600'
                              }`}>
                                RD$ {formatMonto(datos.diferencias_caja.totales.diferencia)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {/* Indicador visual de diferencias */}
                      {Math.abs(datos.diferencias_caja.totales.diferencia) > 0 && (
                        <div className={`mt-4 p-4 rounded-xl border ${
                          datos.diferencias_caja.totales.diferencia > 0 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <p className={`text-sm font-medium ${
                            datos.diferencias_caja.totales.diferencia > 0 
                              ? 'text-blue-700' 
                              : 'text-red-700'
                          }`}>
                            {datos.diferencias_caja.totales.diferencia > 0 
                              ? 'Sobrante detectado en caja(s)' 
                              : 'Faltante detectado en caja(s)'
                            }
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No hay cierres de caja registrados para esta fecha</p>
                    </div>
                  )}
                </div>
                </div>

                {/* INFORMACIÓN ADICIONAL */}
                {datos.metricas_generales.cantidad_facturas > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-cascadia text-xl font-bold text-gray-800">Detalles Adicionales</h2>
                    
                    <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700 font-medium">Cantidad de Facturas:</span>
                        <span className="text-sm font-bold text-gray-900">
                          {datos.metricas_generales.cantidad_facturas}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700 font-medium">Egresos por Compras:</span>
                        <span className="text-sm font-bold text-gray-900">
                          RD$ {formatMonto(datos.metricas_generales.total_egresos_compra)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700 font-medium">Egresos por Gastos Menores:</span>
                        <span className="text-sm font-bold text-gray-900">
                          RD$ {formatMonto(datos.metricas_generales.total_egresos_gasto)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700 font-medium">Ticket Promedio:</span>
                        <span className="text-sm font-bold text-gray-900">
                          RD$ {formatMonto(
                            datos.metricas_generales.cantidad_facturas > 0
                              ? datos.metricas_generales.total_ventas / datos.metricas_generales.cantidad_facturas
                              : 0
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  </div>
                )}
              </>
            )}

            {/* Si no hay datos */}
            {datos && datos.metricas_generales.total_ventas === 0 && datos.metricas_generales.total_ingresos === 0 && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-12 text-center">
                <p className="text-gray-500 text-lg">No hay movimientos registrados para esta fecha</p>
                <p className="text-gray-400 text-base mt-2">Selecciona otra fecha para ver los movimientos</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
